/**
 * @description 生成视频的模型
 */
export const enum SceneModel {
  RM = 'rm',
  WAN = 'wan',
  LORA = 'lora',
  RM2_5 = 'rm2.5',
  RM2_6_PRO = '2.6pro'
}

/**
 * @description 生成视频的场景ID
 */
export const enum SceneID {
  RM = '1',
  LIVE_ME = '2',
  BGM = '3',
  REMIX = '4',
  /**
   * @deprecated wan 模型已经删除了，字段暂时保留，但是不使用
   */
  WAN = '5',
  /**
   * 对于前端来说，7 是 RM2.5，但是对于后端，还需要满足 engine === '2.5'
   */
  RM2_5 = '7',
  REMIX_TEMPLATE = '8',
  /**
   * 对于前端来说，9 是 Turbo，但是对于后端，还需要满足 engine === 'turbo'
   */
  TURBO = '9',
  RM2_6 = '10',
  LIPM3 = '11',
  USER_UPLOAD = '12',
  ACT_MOTION = '13',
  ACT_MOTION_V2 = '39',
  RM2_6_PRO = '15',
  SORA2 = '16',
  /** Sora 2 Max，engine = sora2-max */
  SORA2_MAX = '32',
  VEO_3 = '17',
  VEO_3_1 = '18',
  REMOVE_WATERMARK = '19',
  MID_JOURNEY = '20',
  SVI = '21',
  REMIX_MODEL = '22',
  AGENT = '23',
  GROK = '25',
  GROK_1_5 = '49',
  RUNWAY = '26',
  UPSCALE = '27',
  INSET_FRAME = '28',
  RM3 = '29',
  KLING = '30',
  SEEDANCE2 = '31',
  RM3_1 = '33',
  RM3_1_TURBO = '35',
  SEEDANCE2_FAST = '36',
  SEEDANCE2_MINI = '55',
  RM3_1_REMIX = '38',
  LIP_GEN_3_1 = '40',
  HAPPYHORSE = '41',
  /** Gemini Omni Flash */
  OMNI = '45',
  /** 导演台提交 sceneId */
  RM3_5 = '56',
  /** Real Motion 3.5 正式模型 */
  RM3_5_MODEL = '61',
  RM3_2_REF = '50',
  /**
   * SEEDANCE2_REF 不是独立模型，是 SEEDANCE2 的 Reference 模式
   * 使用占位符 ID，仅用于前端 Model Limit 显示区分
   */
  SEEDANCE2_REF = 'seedance2-ref',
  /**
   * SEEDANCE2_FAST_REF 不是独立模型，是 SEEDANCE2_FAST 的 Reference 模式
   * 使用占位符 ID，仅用于前端 Model Limit 显示区分
   */
  SEEDANCE2_FAST_REF = 'seedance2-fast-ref',
  /**
   * SEEDANCE2_MINI_REF 不是独立模型，是 SEEDANCE2_MINI 的 Reference 模式
   * 使用占位符 ID，仅用于前端 Model Limit 显示区分
   */
  SEEDANCE2_MINI_REF = 'seedance2-mini-ref',
  /**
   * Voice Design 音频生成场景
   */
  VOICE_DESIGN = '46',
  /**
   * Voice Clone 音频克隆场景
   */
  VOICE_CLONE = '47'
}

export const TOOL_TASKS = [SceneID.UPSCALE, SceneID.REMOVE_WATERMARK, SceneID.INSET_FRAME]

/**
 * @description 生成视频的引擎
 */
export const enum SceneEngine {
  RM2_5 = '2.5',
  RM2_6 = '2.6',
  TURBO = 'turbo',
  /**
   * lip motion GEN-3
   */
  LIPM3 = 'lipm3',
  ACT_MOTION = 'unianimate',
  ACT_MOTION_V2 = 'unianimate_v2',
  RM2_6_PRO = '2.6pro',
  SORA2 = 'sora2',
  // sora2 25s
  SORA2_PRO = 'sora2-pro',
  SORA2_MAX = 'sora2-max',
  VEO_3 = 'veo3',
  VEO_3_1 = 'veo3.1',
  /**
   * @description 普通去水印
   */
  REMOVE_WATERMARK = 'rem-wm',
  /**
   * Sora 模式
   */
  REMOVE_WATERMARK_SORA = 'sora-rem-wm',
  MID_JOURNEY = 'mj',
  SVI = 'svi',
  REMIX = 'remix',
  AGENT = 'mini-sora',
  GROK = 'grok-video',
  GROK_1_5 = 'grok15-video',
  RUNWAY = '3.0',
  UPSCALE = 'video-upscale',
  INSET_FRAME = 'vfi',
  RM3 = '3.0A',
  RM3_1 = 'rm3.1',
  RM3_1_TURBO = 'rm3.1-turbo',
  RM3_1_FL2V = 'rm3.1-fl2v',
  RM3_1_FL2V_TURBO = 'rm3.1-fl2v-turbo',
  RM3_2 = 'rm3.2',
  /** Real Motion 3.2 Remix */
  RM3_2_REMIX = 'rm3.2-remix-i2v',
  /** Real Motion 3.5 正式模型（双模式） */
  RM3_5_MODEL = 'rm3.5',
  /** Real Motion 3.5 Fast */
  RM3_5_FAST = 'rm3.5-fast',
  /** 导演台提交引擎（与 RM3.5 模型不同） */
  RM3_5 = 'rm3.5-director',
  RM3_2_REF = 'rm3.2-ref',
  /** RM3.2D：自动导演台模型（一键自动生成导演台视频） */
  RM3_2D = 'rm3.2d',
  KLING = 'kling3',
  SEEDANCE2 = 'seedance2',
  SEEDANCE2_MINI = 'seedance2-mini',
  HappyHorse = 'hh',
  /** Gemini Omni Flash */
  OMINI_FLASH = 'omni-flash',
  /** Voice Design 音频生成 */
  BREEZE_VOICE_DESIGN = 'breeze-voice-design',
  /** Voice Clone 声音克隆 */
  BREEZE_VOICE_CLONE = 'breeze-voice-clone'
}

export const enum ConversationStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export const enum MessageRole {
  USER = 'user',
  ASSISTANT = 'assistant'
}

export const enum ConversationTaskStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

/**
 * @description agent 图片/资产来源
 */
export const enum ImageSource {
  /**
   * @description 上传图片
   */
  UPLOAD = 'upload',
  /**
   * @description 参考图片
   */
  REFERENCE = 'reference',
  /**
   * @description 生成图片
   */
  GENERATED = 'generated',
  /**
   * @description 用户上传（注册资产返回）
   */
  USER_UPLOAD = 'user_upload'
}

/**
 * @description Agent 名称（开放字符串，常见值列举于此）
 * 文档中 agent 字段为开放字符串，如 "screenwriter"、"orchestrator" 等
 */
export const enum AgentType {
  ORCHESTRATOR = 'orchestrator',
  SCREENWRITER = 'screenwriter',
  IMAGE_GENERATOR = 'image_generator',
  VIDEO_GENERATOR = 'video_generator'
}

/** 存储提供商 */
export const enum StorageProvider {
  AWS = 'aws',
  WASABI = 'wasabi'
}

/** 资产类型 */
export const enum AssetType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document'
}

/** 文档解析状态 */
export const enum ParsingStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

/** SSE 事件类型 */
export const enum EventType {
  START = 'start',
  DONE = 'done',
  ERROR = 'error',
  AGENT = 'agent',
  TOOL = 'tool',
  ITERATION = 'iteration',
  CHUNK = 'chunk',
  THINKING = 'thinking',
  ASSET = 'asset',
  MESSAGE = 'message',
  /** @deprecated 已弃用，使用 AWAIT_CONFIRMATION 代替 */
  USER_APPROVAL_REQUIRED = 'user_approval_required',
  /** 等待用户确认事件（显示继续按钮） */
  AWAIT_CONFIRMATION = 'await_confirmation',
  /** 引导信息事件（工作流进度、推荐问题等） */
  GUIDANCE = 'guidance',
  /** 心跳保活事件 */
  HEARTBEAT = 'heartbeat'
}

/** SSE 事件阶段 */
export const enum EventPhase {
  START = 'start',
  END = 'end',
  PROGRESS = 'progress',
  PLACEHOLDER = 'placeholder'
}

/** SSE 事件执行状态 */
export const enum EventStatus {
  SUCCESS = 'success',
  ERROR = 'error'
}

export const enum AgentTool {
  GENERATE_IMAGE = 'generate_image',
  GENERATE_VIDEO = 'generate_video',
  GENERATE_TTS = 'generate_tts',
  WEB_SEARCH = 'search_web'
}
